import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { CameraCapture } from "../components/CameraCapture";
import { api, compressImage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Upload, X, Sparkles, Loader2, Camera, ShieldCheck, RefreshCw, Ruler } from "lucide-react";

const OCCASIONS = [
  "Wedding",
  "Party",
  "Office",
  "Date Night",
  "Festival",
  "Beach",
  "Formal Event",
  "Casual Outing",
];

const PHOTO_STORAGE_KEY = "styleme_session_photo";
const FIT_KEY = "styleme_fit";

const MEAS_FIELDS = {
  female: ["height", "bust", "waist", "hip", "belly"],
  male: ["height", "shoulder", "waist", "belly"],
};
const MEAS_RANGES_CM = {
  height: [140, 200],
  bust: [70, 130],
  waist: [55, 125],
  hip: [80, 140],
  shoulder: [38, 58],
  belly: [60, 150],
};
const MEAS_SKETCHES = {
  female: {
    height: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/161d654d7dc0b585839950f9a55f522c40a6376c6607fa37d040d7c8a9689ecd.jpeg",
    bust: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/77203b1135e4a2dcef7e2dd5b02d657526cf0fa1b9e1530e2c8e8d04eca8e6f6.jpeg",
    waist: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/40b7b3858a5091d9fb905a4bb9f8bf778a54afec2010c14c32725effe86aad27.jpeg",
    hip: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/ecea26b79a449f7ba5aa96f850747f5f2d758a1074c4c01a7a88b73b39f8c7bf.jpeg",
    belly: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/60644b92c539c908ca05bd3476a81d78ac794b0f36d8a88e45094226df59b2e1.jpeg",
  },
  male: {
    height: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/f46a00db1258baa6559d8cc14a448e7c51bb700e6249e44e3a8520810f3bc2ec.jpeg",
    shoulder: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/8e393a5b68a3243914c0e954dc02acc427b8a2dc4bee60eb8f9b58766757ee82.jpeg",
    waist: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/3884dbfecec30aeaea4ec5d20e0c95c3be0ab67b0c629771bed479f1b54256df.jpeg",
    belly: "https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/ce12407f666725aba49f4fbbbe39c24b39d91e2b6b5445f1929804248e6ff24a.jpeg",
  },
};
const MEAS_HINTS = {
  height: "Stand straight against a wall — measure from the top of your head to the floor.",
  bust: "Wrap the tape around the fullest part of your bust, keeping it level all the way round.",
  waist: "Around your natural waist — the narrowest point, just above the navel.",
  hip: "Around the widest part of your hips and seat, feet together.",
  shoulder: "Straight across the back, from one shoulder tip to the other.",
  belly: "Around the fullest part of your stomach, at navel level. This helps us drape outfits honestly — like a true mirror.",
};
const rangeOptions = (field, unit) => {
  const [lo, hi] = MEAS_RANGES_CM[field];
  const a = unit === "inch" ? Math.round(lo / 2.54) : lo;
  const b = unit === "inch" ? Math.round(hi / 2.54) : hi;
  return Array.from({ length: b - a + 1 }, (_, i) => a + i);
};
const selectCls =
  "w-full bg-[#17161C] border border-white/15 rounded-sm px-3 py-2.5 text-sm text-[#F7F5F2] outline-none focus:border-[#E3C2B0] transition-colors";

export default function Studio() {
  // Persistent photo: rehydrate from localStorage so the user can compose
  // multiple looks for the same portrait without re-uploading each time.
  const [photo, setPhoto] = useState(() => {
    try { return localStorage.getItem(PHOTO_STORAGE_KEY) || null; } catch { return null; }
  });
  const [photoPreview, setPhotoPreview] = useState(() => {
    try { return localStorage.getItem(PHOTO_STORAGE_KEY) || null; } catch { return null; }
  });
  const [occasion, setOccasion] = useState("");
  const [notes, setNotes] = useState("");
  const [fit, setFit] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FIT_KEY)) || { gender: "", unit: "cm" }; }
    catch { return { gender: "", unit: "cm" }; }
  });
  const [outfitUrl, setOutfitUrl] = useState("");
  const [outfitImage, setOutfitImage] = useState(null);
  const outfitFileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [activeMeas, setActiveMeas] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Keep the persisted photo in sync with state.
  useEffect(() => {
    try {
      if (photo) localStorage.setItem(PHOTO_STORAGE_KEY, photo);
      else localStorage.removeItem(PHOTO_STORAGE_KEY);
    } catch { /* quota or private-mode browsers */ }
  }, [photo]);

  useEffect(() => {
    try { localStorage.setItem(FIT_KEY, JSON.stringify(fit)); } catch { /* ignore */ }
  }, [fit]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image must be under 20MB");
      return;
    }
    const b64 = await compressImage(file);
    if (!b64) {
      toast.error("Could not read this image. Try another file.");
      return;
    }
    setPhoto(b64);
    setPhotoPreview(b64);
  };

  const changePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    // Open the file picker straight away so the action feels seamless.
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleOutfitFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    const b64 = await compressImage(file);
    if (!b64) {
      toast.error("Could not read this image. Try another file.");
      return;
    }
    setOutfitImage(b64);
    setOutfitUrl("");
  };

  const submit = async () => {
    if (!photo) return toast.error("Upload a portrait first");
    if (!fit.gender) return toast.error("Select male or female under Your Fit");
    if (!occasion) return toast.error("Choose an occasion");
    setLoading(true);
    const startedAt = Date.now();
    try {
      const measurements = {};
      (MEAS_FIELDS[fit.gender] || []).forEach((f) => {
        if (fit[f]) measurements[f] = Number(fit[f]);
      });
      const { data } = await api.post("/looks/generate", {
        photo_base64: photo,
        occasion,
        notes,
        gender: fit.gender,
        measurements: Object.keys(measurements).length
          ? { ...measurements, unit: fit.unit }
          : null,
        outfit_link: outfitUrl.trim() || null,
        outfit_image_base64: outfitImage || null,
      });
      toast.success("Your look is ready.");
      navigate(`/look/${data.id}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Your trial has ended. Subscribe to continue.");
        navigate("/subscription");
        return;
      }

      // Recovery path: backend may have completed the generation but the
      // response was cut off by an ingress/proxy timeout. Check if a fresh
      // look has been written for this user since we started.
      const isLikelyTimeout =
        err?.code === "ECONNABORTED" ||
        !err?.response ||
        err?.response?.status === 502 ||
        err?.response?.status === 504;
      if (isLikelyTimeout) {
        try {
          const { data: latest } = await api.get("/looks/latest");
          if (latest?.id && latest?.created_at) {
            const createdMs = new Date(latest.created_at).getTime();
            if (createdMs >= startedAt - 5000) {
              toast.success("Your look is ready.");
              navigate(`/look/${latest.id}`);
              return;
            }
          }
        } catch {
          /* ignore recovery failure, fall through to error toast */
        }
      }

      if (err?.code === "ECONNABORTED") {
        toast.error("Generation took too long. The AI is busy — please try again in a moment.");
      } else {
        toast.error(err?.response?.data?.detail || "Generation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 sm:py-16">
        <div className="fade-up max-w-2xl">
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">The Studio</span>
          <h1 className="font-serif text-4xl sm:text-6xl mt-4 font-light leading-tight">
            Compose your look.
          </h1>
          <p className="mt-6 text-[#A8A6A1] leading-relaxed">
            Begin with a clear, well-lit portrait. Whisper the occasion. Add any preference —
            a color you love, a fabric you trust, a memory to honour.
          </p>
          {user && !user.is_active && (
            <div data-testid="studio-trial-ended" className="mt-8 border border-red-500/20 bg-red-500/5 px-6 py-4 rounded-sm flex items-center justify-between gap-4 flex-wrap">
              <span className="text-xs uppercase tracking-[0.3em] text-red-200">
                Trial ended — subscribe to continue composing
              </span>
              <button
                onClick={() => navigate("/subscription")}
                data-testid="studio-subscribe-cta"
                className="text-xs uppercase tracking-[0.3em] bg-[#E3C2B0] text-[#0A0908] px-4 py-2 rounded-sm hover:bg-[#F0D4C5]"
              >
                See Plans
              </button>
            </div>
          )}
          {user?.plan === "free" && user?.is_active && (
            <div className="mt-8 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] flex items-center gap-4 flex-wrap">
              <span>Free member</span>
              <span>·</span>
              <span data-testid="studio-free-remaining">{user.free_looks_remaining} of 3 free looks remaining</span>
            </div>
          )}
          {user?.is_admin && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-sm text-amber-300">
              <Sparkles size={14} />
              <span data-testid="studio-admin-badge">Admin · Unlimited looks</span>
            </div>
          )}
          {(user?.plan === "basic" || user?.plan === "standard" || user?.plan === "premium") && user?.is_active && (
            <div className="mt-8 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] flex items-center gap-4 flex-wrap">
              <span className="capitalize">{user.plan}</span>
              <span>·</span>
              <span data-testid="studio-daily-remaining">
                {user.daily_looks_remaining} of {user.daily_looks_limit} look{user.daily_looks_limit === 1 ? "" : "s"} left today
              </span>
              <span>·</span>
              <span>renews {new Date(user.subscription_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
            </div>
          )}
        </div>

        <div className="mt-10 sm:mt-16 grid lg:grid-cols-12 gap-8">
          {/* Upload */}
          <div className="lg:col-span-7">
            <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-4 flex items-center justify-between">
              <span>01 · Your Portrait</span>
              <span className="flex items-center gap-4">
                {photoPreview && (
                  <button
                    onClick={changePhoto}
                    data-testid="studio-change-photo"
                    className="inline-flex items-center gap-2 text-[#E3C2B0] hover:text-[#F0D4C5] text-[10px] uppercase tracking-[0.3em]"
                  >
                    <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Change Photo
                  </button>
                )}
                <button
                  onClick={() => setShowCamera(true)}
                  data-testid="studio-open-camera"
                  className="inline-flex items-center gap-2 text-[#E3C2B0] hover:text-[#F0D4C5] text-[10px] uppercase tracking-[0.3em]"
                >
                  <Camera className="w-3 h-3" strokeWidth={1.5} /> Use Camera
                </button>
              </span>
            </label>
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => !photoPreview && fileRef.current?.click()}
              data-testid="upload-zone"
              className={`relative border border-dashed border-white/20 rounded-sm aspect-[4/5] flex items-center justify-center transition-all overflow-hidden group ${photoPreview ? "cursor-default" : "cursor-pointer hover:bg-white/5 hover:border-[#E3C2B0]/50"}`}
            >
              {photoPreview ? (
                <>
                  <img src={photoPreview} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPhoto(null);
                      setPhotoPreview(null);
                    }}
                    data-testid="upload-clear"
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/70 backdrop-blur-md flex items-center justify-center hover:bg-black transition-colors"
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </>
              ) : (
                <div className="text-center p-12">
                  <Upload className="w-8 h-8 text-[#E3C2B0] mx-auto mb-6" strokeWidth={1} />
                  <p className="font-serif text-2xl mb-2">Drop your portrait here</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">
                    or click to browse · jpg, png · max 20mb · auto-compressed
                  </p>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="upload-input"
              />
            </div>

            {/* Privacy reassurance banner — visible directly below the upload */}
            <div
              data-testid="privacy-banner"
              className="mt-4 flex items-start gap-3 p-4 rounded-sm border border-[#E3C2B0]/30 bg-[#E3C2B0]/[0.04]"
            >
              <ShieldCheck className="w-4 h-4 text-[#E3C2B0] mt-[2px] flex-shrink-0" strokeWidth={1.5} />
              <p className="text-[11px] leading-relaxed text-[#F7F5F2]/80">
                <span className="text-[#E3C2B0] uppercase tracking-[0.2em] text-[10px] font-medium">Your privacy matters.</span>
                <span className="block mt-1">
                  Uploaded photos are processed in real-time and permanently deleted immediately after generation.
                  We only store your User ID to manage your session.
                </span>
              </p>
            </div>
          </div>

          {/* Occasion + notes */}
          <div className="lg:col-span-5 space-y-12">
            {photoPreview && (
              <div data-testid="fit-panel" className="fade-up">
                <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-4">
                  02 · Your Fit
                </label>
                <div className="flex items-center gap-6 flex-wrap">
                  {["female", "male"].map((g) => (
                    <label key={g} className="flex items-center gap-2 cursor-pointer text-sm capitalize text-[#F7F5F2]">
                      <input
                        type="radio"
                        name="fit-gender"
                        value={g}
                        checked={fit.gender === g}
                        onChange={() => setFit((f) => ({ gender: g, unit: f.unit }))}
                        data-testid={`fit-gender-${g}`}
                        className="accent-[#E3C2B0] w-4 h-4"
                      />
                      {g}
                    </label>
                  ))}
                  <select
                    value={fit.unit}
                    onChange={(e) => setFit((f) => ({ gender: f.gender, unit: e.target.value }))}
                    data-testid="fit-unit"
                    className={`${selectCls} !w-auto`}
                  >
                    <option value="cm">cm</option>
                    <option value="inch">inch</option>
                  </select>
                </div>
                {fit.gender && (
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    {MEAS_FIELDS[fit.gender].map((fld) => (
                      <div key={fld}>
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#A8A6A1] mb-1.5 flex items-center gap-1.5">
                          {fld}
                          <button
                            type="button"
                            onClick={() => setActiveMeas(activeMeas === fld ? null : fld)}
                            data-testid={`fit-guide-${fld}`}
                            aria-label={`Where to measure ${fld}`}
                            className="text-[#E3C2B0] hover:text-[#F0D4C5] transition-colors"
                          >
                            <Ruler className="w-3 h-3" strokeWidth={1.5} />
                          </button>
                        </span>
                        <select
                          value={fit[fld] || ""}
                          onFocus={() => setActiveMeas(fld)}
                          onChange={(e) => {
                            setFit((f) => ({ ...f, [fld]: e.target.value }));
                            setTimeout(() => setActiveMeas(null), 600);
                          }}
                          data-testid={`fit-${fld}`}
                          className={selectCls}
                        >
                          <option value="">Select</option>
                          {rangeOptions(fld, fit.unit).map((v) => (
                            <option key={v} value={v}>{v} {fit.unit}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                {fit.gender && (
                  <p className="mt-3 text-[11px] text-[#A8A6A1] leading-relaxed">
                    We build a full-length figure from these measurements, so outfits drape true to your body.
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-4">
                {photoPreview ? "03" : "02"} · The Occasion
              </label>
              <div className="grid grid-cols-2 gap-3">
                {OCCASIONS.map((o) => (
                  <button
                    key={o}
                    onClick={() => setOccasion(o)}
                    data-testid={`occasion-${o.toLowerCase().replace(/ /g, "-")}`}
                    className={`text-xs uppercase tracking-[0.25em] py-4 rounded-sm border transition-all ${
                      occasion === o
                        ? "bg-[#E3C2B0] text-[#0A0908] border-[#E3C2B0]"
                        : "bg-transparent text-[#A8A6A1] border-white/10 hover:border-[#E3C2B0]/50 hover:text-[#F7F5F2]"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>

            <div data-testid="outfit-source-panel">
              <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-2">
                {photoPreview ? "04" : "03"} · The Outfit (optional)
              </label>
              <p className="text-[11px] text-[#E3C2B0] mb-3 leading-relaxed">
                Paste a link to an outfit, or upload its photo (even worn by a model) — we'll capture its
                texture, color, pattern and design, and fit that exact outfit to you.
              </p>
              <input
                type="url"
                value={outfitUrl}
                onChange={(e) => setOutfitUrl(e.target.value)}
                placeholder="https:// outfit link (Amazon product page, image link...)"
                data-testid="outfit-link-input"
                disabled={!!outfitImage}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] text-sm font-light transition-colors placeholder:text-[#A8A6A1]/50 disabled:opacity-40"
              />
              <div className="mt-4 flex items-center gap-4">
                {outfitImage ? (
                  <div className="relative">
                    <img
                      src={outfitImage}
                      alt="Outfit to try on"
                      data-testid="outfit-image-preview"
                      className="h-28 w-24 object-cover rounded-sm border border-[#E3C2B0]/40"
                    />
                    <button
                      onClick={() => setOutfitImage(null)}
                      data-testid="outfit-image-clear"
                      aria-label="Remove outfit photo"
                      className="absolute -top-2 -right-2 bg-[#17161C] border border-white/20 rounded-full p-1 text-[#A8A6A1] hover:text-[#F7F5F2] transition-colors"
                    >
                      <X className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => outfitFileRef.current?.click()}
                    data-testid="outfit-image-upload"
                    className="flex items-center gap-2 border border-dashed border-white/20 hover:border-[#E3C2B0] px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.2em] text-[#A8A6A1] hover:text-[#F7F5F2] transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Upload outfit photo
                  </button>
                )}
                <input
                  ref={outfitFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { handleOutfitFile(e.target.files?.[0]); e.target.value = ""; }}
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-2">
                {photoPreview ? "05" : "04"} · Whisper (optional)
              </label>
              <p className="text-[11px] text-[#A8A6A1] mb-3 leading-relaxed">
                Any styling wishes — mood, colors, modesty, favourite era.
              </p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., 'temple wedding at sunset, warm tones'"
                data-testid="studio-notes"
                rows={4}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] text-base font-light transition-colors resize-none placeholder:text-[#A8A6A1]/50"
              />
              {/https?:\/\//.test(notes) && (
                <p data-testid="studio-link-detected" className="mt-2 text-xs text-[#E3C2B0] flex items-center gap-2">
                  <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                  Outfit link detected — we'll fetch it and show it on you.
                </p>
              )}
            </div>

            <button
              onClick={submit}
              disabled={loading || !photo || !occasion}
              data-testid="studio-generate"
              className="w-full bg-[#E3C2B0] text-[#0A0908] py-5 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  Crafting your look (~30s)
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  Compose My Look
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {activeMeas && fit.gender && MEAS_SKETCHES[fit.gender][activeMeas] && (
        <div
          data-testid="meas-sketch-popup"
          className="fixed z-50 bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 bg-[#17161C] border border-[#E3C2B0]/30 rounded-sm shadow-2xl shadow-black/60 overflow-hidden fade-up"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#E3C2B0] flex items-center gap-2">
              <Ruler className="w-3 h-3" strokeWidth={1.5} />
              How to measure · {activeMeas}
            </span>
            <button
              onClick={() => setActiveMeas(null)}
              data-testid="meas-sketch-close"
              aria-label="Close measurement guide"
              className="text-[#A8A6A1] hover:text-[#F7F5F2] transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <img
            src={MEAS_SKETCHES[fit.gender][activeMeas]}
            alt={`Where to measure ${activeMeas}`}
            className="w-full h-56 sm:h-72 object-contain bg-[#17161C]"
          />
          <p className="px-4 py-3 text-[11px] leading-relaxed text-[#F7F5F2]/80 border-t border-white/10">
            {MEAS_HINTS[activeMeas]}
          </p>
        </div>
      )}
      {showCamera && (
        <CameraCapture
          onCapture={(b64) => {
            setPhoto(b64);
            setPhotoPreview(b64);
            setShowCamera(false);
          }}
          onCancel={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}
